/**
 * The LOI tab's copy, as pure functions (docs/ux/loi.md): value formatting
 * per field kind, confidence words, the blocking and state sentences, the
 * decision sentences and the accessible names. Pure so a test can assert the
 * words and prove that no machine value reaches a sentence outside the two
 * panes: everything here that carries a value is used only inside them.
 */

import { reportTypeLabel, type ReportTypeKind } from '@beam/core/status';
import type { LoiDecision, LoiFieldView, LoiHead, LoiQuestionView, LoiValue } from '@/app/types';
import type { DecisionWord } from '../casefile/describe';
import { longDate } from '@/lib/format';

export const LOW = 0.6;
export const HIGH = 0.85;

export const DECISION_WORD: Record<LoiDecision, DecisionWord> = { unresolved: 'Unresolved', accepted: 'Accepted', corrected: 'Corrected', missing: 'Missing', flagged: 'Flagged' };

export function confidenceWord(c: number): 'low' | 'medium' | 'high' {
  return c < LOW ? 'low' : c < HIGH ? 'medium' : 'high';
}

export const confidenceText = (c: number): string => `${c.toFixed(2)}, ${confidenceWord(c)}`;

const REPORT_TYPES = new Set(['ime', 'fitness_for_work', 'permanent_impairment', 'mva', 'supplementary', 'file_review', 'consultation_advice']);

export function optionLabel(key: string, value: string): string {
  return key === 'reportType' && REPORT_TYPES.has(value) ? reportTypeLabel(value as ReportTypeKind, 'label') : value;
}

/** A field value for a cell or a pair; `absent` is the word for null. */
export function formatValue(field: Pick<LoiFieldView, 'key' | 'kind'>, value: LoiValue, absent: string): string {
  if (value === null || value === undefined) return absent;
  if (Array.isArray(value)) return value.length === 0 ? absent : `${value.length} ${value.length === 1 ? 'entry' : 'entries'}: ${value.join(', ')}`;
  if (field.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return longDate(value);
  if (field.kind === 'enum') return optionLabel(field.key, value);
  return value;
}

export const MACHINE_ABSENT = 'Nothing read';
export const REVIEWED_ABSENT = 'Not in the letter';

/** The value a decision settles on, mirroring the server's rule. */
export function settledValue(field: LoiFieldView): LoiValue {
  const item = field.item;
  if (!item) return null;
  switch (item.decision) {
    case 'accepted': return item.machineValue;
    case 'corrected': return item.reviewedValue;
    case 'missing': return null;
    default: return null;
  }
}

export type Counts = { total: number; decided: number; optionalUndecided: number; requiredUndecided: number; requiredMissing: number; requiredFlagged: number; otherFlagged: number; questionsUndecided: number; questionsFlagged: number; accepted: number; corrected: number; missing: number; flagged: number };

export function countsOf(fields: LoiFieldView[], questions: LoiQuestionView[]): Counts {
  const fd = (f: LoiFieldView): LoiDecision => f.item?.decision ?? 'unresolved';
  const qd = (q: LoiQuestionView): LoiDecision => q.item?.decision ?? 'unresolved';
  const all: LoiDecision[] = [...fields.map(fd), ...questions.map(qd)];
  return {
    total: all.length,
    decided: all.filter((d) => d !== 'unresolved').length,
    optionalUndecided: fields.filter((f) => !f.required && fd(f) === 'unresolved').length,
    requiredUndecided: fields.filter((f) => f.required && fd(f) === 'unresolved').length,
    requiredMissing: fields.filter((f) => f.required && fd(f) === 'missing').length,
    requiredFlagged: fields.filter((f) => f.required && fd(f) === 'flagged').length,
    otherFlagged: fields.filter((f) => !f.required && fd(f) === 'flagged').length,
    questionsUndecided: questions.filter((q) => qd(q) === 'unresolved').length,
    questionsFlagged: questions.filter((q) => qd(q) === 'flagged').length,
    accepted: all.filter((d) => d === 'accepted').length,
    corrected: all.filter((d) => d === 'corrected').length,
    missing: all.filter((d) => d === 'missing').length,
    flagged: all.filter((d) => d === 'flagged').length,
  };
}

export function blockingCount(c: Counts): number {
  return c.requiredUndecided + c.requiredMissing + c.requiredFlagged + c.otherFlagged + c.questionsUndecided + c.questionsFlagged;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export function blockingSentence(c: Counts, version: number): string {
  const n = blockingCount(c);
  if (n === 0 && c.optionalUndecided > 0) return `Nothing blocks approval. ${plural(c.optionalUndecided, 'optional field is', 'optional fields are')} undecided and would be empty in the form. Approve LOI writes the authoritative form, version ${version + 1}.`;
  if (n === 0) return `Every item is decided. Approve LOI writes the authoritative form, version ${version + 1}.`;
  const parts: string[] = [];
  if (c.requiredUndecided) parts.push(`${plural(c.requiredUndecided, 'required field', 'required fields')} undecided`);
  if (c.requiredMissing) parts.push(`${plural(c.requiredMissing, 'required field', 'required fields')} missing`);
  if (c.requiredFlagged) parts.push(`${plural(c.requiredFlagged, 'required field', 'required fields')} flagged`);
  if (c.otherFlagged) parts.push(`${plural(c.otherFlagged, 'field', 'fields')} flagged`);
  if (c.questionsUndecided) parts.push(`${plural(c.questionsUndecided, 'question', 'questions')} undecided`);
  if (c.questionsFlagged) parts.push(`${plural(c.questionsFlagged, 'question', 'questions')} flagged`);
  return `${n} ${n === 1 ? 'item blocks' : 'items block'} approval: ${parts.join(', ')}.`;
}

export const clockOf = (iso: string): string => new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false });
const clock = clockOf;
const dateAt = (iso: string): string => `${longDate(iso)} at ${clock(iso)}`;

export function stateSentence(head: LoiHead, c: Counts, mine: boolean, rerun: boolean): string {
  if (head.state === 'approved' && head.approvedAt) {
    return `Approved by ${head.approvedByName ?? head.approvedBy ?? 'someone'} on ${dateAt(head.approvedAt)}. Authoritative form version ${head.authoritativeVersion}. Decisions are locked. The extraction file is kept as it is.`;
  }
  if (!head.startedAt && c.decided === 0) {
    if (rerun && head.extractedAt) return `Re-extracted ${dateAt(head.extractedAt)}. Decisions from the earlier run are shown as previous decisions and are not applied. 0 of ${c.total} decided.`;
    return `${head.extractedAt ? `Extracted ${dateAt(head.extractedAt)}. ` : ''}Nobody has started the review. 0 of ${c.total} decided.`;
  }
  const who = mine ? 'you' : head.reviewerName ?? head.reviewer ?? 'someone';
  const since = head.startedAt ? ` since ${clock(head.startedAt)}` : '';
  const flagged = c.flagged > 0 ? `; ${c.flagged} flagged` : '';
  return `In review by ${who}${since}. ${c.decided} of ${c.total} decided${flagged}.`;
}

/** "accepted automatically (confidence 0.92)": the machine's acceptance (D-0068), until a person re-decides. */
export function autoPhrase(auto: { confidence: number }): string {
  return `accepted automatically (confidence ${auto.confidence.toFixed(2)})`;
}

export function decisionSentence(decision: LoiDecision, byName: string | undefined, at: string | undefined, auto?: { confidence: number } | null): string {
  const who = byName ? ` by ${byName}` : '';
  const when = at ? `, ${clockOf(at)}` : '';
  if (decision === 'accepted' && auto) return `${autoPhrase(auto).replace(/^a/, 'A')}${when}`;
  switch (decision) {
    case 'accepted': return `Accepted${who}${when}`;
    case 'corrected': return `Corrected${who}${when}`;
    case 'missing': return `Missing${who}${when}`;
    case 'flagged': return `Flagged${who}${when}`;
    default: return 'Not decided';
  }
}

/** "by Name, 14:26" under a reviewed value; "accepted automatically (confidence 0.92), 14:02" while the acceptance is the machine's. */
export function byLine(byName: string | undefined, at: string | undefined, auto?: { confidence: number } | null): string {
  return [auto ? autoPhrase(auto) : byName ? `by ${byName}` : '', at ? clockOf(at) : ''].filter(Boolean).join(', ');
}

/** The hub's separator between facts on one line. */
export const SEP = ' \u00b7 ';

/** "Part (a) of question 2." for a nested node; the label itself for a top-level question. */
export function questionName(q: Pick<LoiQuestionView, 'id' | 'label' | 'depth' | 'number'>): string {
  const clean = (s: string) => s.replace(/\.$/, '');
  if (q.depth === 0) return `Question ${clean(q.label)}`;
  const parent = q.number.slice(0, q.number.length - q.label.length);
  return `Part ${clean(q.label)} of question ${clean(parent)}`;
}

export const rowName = (field: LoiFieldView): string => {
  const machine = field.machine ? `${formatValue(field, field.machine.value, MACHINE_ABSENT)}, confidence ${confidenceText(field.machine.confidence)}${field.machine.evidence ? `, page ${field.machine.evidence.page}` : ''}` : 'no extraction';
  const reviewed = field.item && field.item.decision !== 'unresolved' && field.item.decision !== 'flagged' ? formatValue(field, settledValue(field), REVIEWED_ABSENT) : 'not decided';
  return `${field.label}${field.required ? ', required' : ''}. Machine: ${machine}. Reviewed: ${reviewed}. ${DECISION_WORD[field.item?.decision ?? 'unresolved']}.`;
};

/** Items whose decision blocks approval, in document order: fields first, then questions. */
export function blockingItems(fields: LoiFieldView[], questions: LoiQuestionView[]): string[] {
  const blocksField = (f: LoiFieldView) => { const d = f.item?.decision ?? 'unresolved'; return (f.required && (d === 'unresolved' || d === 'missing')) || d === 'flagged'; };
  const blocksQuestion = (q: LoiQuestionView) => { const d = q.item?.decision ?? 'unresolved'; return d === 'unresolved' || d === 'flagged'; };
  return [...fields.filter(blocksField).map((f) => f.key), ...questions.filter(blocksQuestion).map((q) => `q:${q.id}`)];
}

/** Every undecided item in document order, for "Next unresolved". */
export function undecidedItems(fields: LoiFieldView[], questions: LoiQuestionView[]): string[] {
  return [...fields.filter((f) => (f.item?.decision ?? 'unresolved') === 'unresolved').map((f) => f.key), ...questions.filter((q) => (q.item?.decision ?? 'unresolved') === 'unresolved').map((q) => `q:${q.id}`)];
}

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function daysIn(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function isoDate(day: number, month: number, year: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * The saved decision a row's draft is typed over: the item's run and time,
 * or nothing for an undecided item. A draft is restored only while this is
 * still the saved decision, so a save landing after the row was left, or
 * another reviewer's decision, never resurfaces as the reviewer's own choice.
 */
export const draftStamp = (item: { runId?: string; at?: string } | null | undefined): string => `${item?.runId ?? ''}|${item?.at ?? ''}`;
